import { renderAgendaMarkdown } from "./agendaMarkdown.js";
import { escapeToHtml } from "./escapeHtml.js";

import type {
  AgendaItem,
  CustomerSafeRichComment,
  CustomerSafeTicket,
  StageBranding,
  StageComment,
  StageDeckItem,
  StageMode,
  StageSnapshot,
  StageTheme,
  TicketSummaryDto,
  UpcomingEvent,
} from "@msc/shared";

/**
 * Kommentare für die Bühne. Primärquelle sind die RICH-Kommentare des Detail-
 * Caches (gerendertes HTML inkl. eingebetteter Screenshots über den
 * Attachment-Proxy). Interne Kommentare kommen NUR durch, wenn ihre Id in
 * `releasedInternal` steht (explizite Lead-Freigabe). Rückfall (Detail noch
 * nicht geladen): öffentliche Klartext-Kommentare, HTML-escaped.
 */
function buildStageComments(o: {
  ticket: CustomerSafeTicket;
  detailComments: CustomerSafeRichComment[] | null;
  releasedInternal: string[];
}): StageComment[] {
  if (o.detailComments) {
    return o.detailComments
      .filter((c) => c.isPublic || o.releasedInternal.includes(c.id))
      .map((c) => ({
        id: c.id,
        authorName: c.authorName,
        createdIso: c.createdIso,
        bodyHtml: c.bodyHtml,
        internal: !c.isPublic,
      }));
  }
  return o.ticket.publicComments.map((c) => ({
    id: c.id,
    authorName: c.authorName,
    createdIso: c.createdIso,
    bodyHtml: escapeToHtml(c.bodyText),
    internal: false,
  }));
}

/**
 * Baut den Snapshot fürs Bühnenfenster — die EINZIGE Stelle, die Bühnen-Daten
 * zusammenstellt. Nimmt bewusst nur kundensichere Quellen entgegen
 * (`CustomerSafeTicket` = fail-closed Allowlist-DTO; Zusammenfassungen
 * basieren auf öffentlichem Input). Rein: keine Seiteneffekte, kein Zugriff
 * auf Stores oder Storage.
 *
 * Port aus der Hub-App (`jira-customer-view/frontend/src/store/presentation.ts`,
 * `buildSnapshot`); einzige Abweichung: eigenständige, reine Funktion statt
 * Teil des Stores. `fallback`/`edited`/Notizen gelangen nie in den Snapshot —
 * dieser Datei entnimmt der Aufrufer nur, was hier explizit gemappt wird.
 */
export function buildStageSnapshot(o: {
  mode: StageMode;
  theme: StageTheme;
  deck: string[];
  currentIndex: number;
  summaries: Record<string, TicketSummaryDto>;
  events: UpcomingEvent[] | null;
  tickets: CustomerSafeTicket[];
  customerName: string;
  reviewedKeys: Set<string>;
  currentDueDate: string | null;
  /** Rich-Kommentare des aktuellen Tickets aus dem Detail-Cache (falls geladen). */
  currentDetailComments: CustomerSafeRichComment[] | null;
  /**
   * Beschreibung des aktuellen Tickets als HTML aus dem Detail-Cache (falls
   * geladen); sonst fällt der Snapshot auf den escapten Klartext zurück.
   */
  currentDescriptionHtml: string | null;
  /** Freigegebene interne Kommentar-Ids des aktuellen Tickets. */
  currentReleasedInternal: string[];
  shownAttachment: { ticketKey: string; id: string; filename: string } | null;
  /** Eingefrorene Gruppe je Ticket (#278); fehlt ein Eintrag, steht das Ticket ohne Gruppe. */
  deckGroups?: Record<string, string>;
  /** Fokussierter Kommentar (#272); zählt nur, wenn er auf der Bühne steht. */
  focusedComment?: { ticketKey: string; id: string } | null;
  /**
   * Die Agenda-Punkte des Termins (D9). Abgewählte bleiben im Cockpit stehen,
   * gehen aber NICHT über den Kanal — auf der Bühne gibt es sie nicht.
   */
  agendaItems: AgendaItem[];
  /** Index in die aktivierten Punkte; −1 außerhalb des Modus `agendaItem`. */
  agendaIndex: number;
  /**
   * Kundendesign (D10): Logo-Adresse und zwei Farben, `null` ohne Design. Geht
   * unverändert auf die Bühne — ob sie es als Theme zeichnet, entscheidet dort
   * `resolveStageTheme` aus `theme` und diesem Feld.
   */
  branding: StageBranding | null;
}): StageSnapshot {
  const byKey = new Map(o.tickets.map((t) => [t.key, t]));
  const deck: StageDeckItem[] = o.deck.map((key) => {
    const group = o.deckGroups?.[key];
    return {
      key,
      title: byKey.get(key)?.title ?? key,
      reviewed: o.reviewedKeys.has(key),
      ...(group !== undefined ? { group } : {}),
    };
  });
  const currentKey = o.mode === "ticket" ? o.deck[o.currentIndex] : undefined;
  const currentTicket = currentKey ? byKey.get(currentKey) : undefined;
  const comments = currentTicket
    ? buildStageComments({
        ticket: currentTicket,
        detailComments: o.currentDetailComments,
        releasedInternal: o.currentReleasedInternal,
      })
    : [];
  // Der Fokus darf nur auf etwas zeigen, das ohnehin auf der Bühne steht —
  // ein nicht freigegebener interner Kommentar ist nicht in `comments`, und
  // ein Fokus auf ihn fällt hier still weg.
  const focusedCommentId =
    currentTicket &&
    o.focusedComment &&
    o.focusedComment.ticketKey === currentTicket.key &&
    comments.some((c) => c.id === o.focusedComment!.id)
      ? o.focusedComment.id
      : null;
  return {
    mode: o.mode,
    theme: o.theme,
    customerName: o.customerName,
    deck,
    currentIndex: o.currentIndex,
    current: currentTicket
      ? {
          ticket: currentTicket,
          descriptionHtml:
            o.currentDescriptionHtml ?? escapeToHtml(currentTicket.descriptionText ?? ""),
          // Nur die erzeugte Zusammenfassung — KEIN Rückfall auf den letzten
          // Kommentar: Der steht ohnehin im Verlauf, und seit die
          // Zusammenfassung abwählbar ist (#236), hätte die Karte sonst bei
          // jedem Ticket einen Kommentar als „Stand" ausgegeben (Bastian,
          // 2026-09-22: „die Ticketbeschreibung wird gar nicht mehr angezeigt").
          summaryText: o.summaries[currentTicket.key]?.text ?? "",
          dueDate: o.currentDueDate,
          comments,
          shownAttachment:
            o.shownAttachment && o.shownAttachment.ticketKey === currentTicket.key
              ? { id: o.shownAttachment.id, filename: o.shownAttachment.filename }
              : null,
          focusedCommentId,
        }
      : null,
    stats:
      o.mode === "end"
        ? { total: deck.length, done: deck.filter((d) => d.reviewed).length }
        : null,
    events: o.mode === "end" ? o.events : null,
    // Markdown wird HIER zu HTML — die Bühne bekommt nie den Rohtext und führt
    // das Ergebnis trotzdem ein zweites Mal durch `sanitizeForDisplay`
    // (Sicherheitsinvariante des Bühnenkanals, Spec D9).
    agendaItems: o.agendaItems
      .filter((a) => a.enabled)
      .map((a) => ({ kind: a.kind, title: a.title, bodyHtml: renderAgendaMarkdown(a.body) })),
    agendaIndex: o.agendaIndex,
    // Nur die drei Felder der Bühne — kein Kunden-Id, keine Herkunft, keine
    // Website: `StageBranding` ist bereits die Allowlist.
    branding: o.branding
      ? {
          logoUrl: o.branding.logoUrl,
          primaryColor: o.branding.primaryColor,
          accentColor: o.branding.accentColor,
        }
      : null,
  };
}
